import React, { useState } from "react";
import type { FollowUpStatusByClient, FollowUpStatusItem } from "../../../types";
import { formatDateForDisplay, getTodayInBrazil } from "../../../utils/dateUtils";
import {
  getFollowUpWhatsAppMessage,
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";
import { Icons, Modal } from "../../shared";
import { CreateAppointmentModal } from "./CreateAppointmentModal";

export function indexFollowUpStatusByClientId(
  clients: FollowUpStatusByClient[],
): Map<number, FollowUpStatusItem[]> {
  return new Map(clients.map((entry) => [entry.clientId, entry.items]));
}

export function getFollowUpItemsForAppointment(
  byClientId: Map<number, FollowUpStatusItem[]> | undefined,
  clientId: number,
  clientCarId?: number | null,
  filterByCar = false,
): FollowUpStatusItem[] {
  const items = byClientId?.get(clientId) ?? [];
  if (!filterByCar) {
    return items;
  }
  if (clientCarId == null) {
    return [];
  }
  return items.filter((item) => item.clientCarId === clientCarId);
}

interface CarExpiredFollowUpReminderProps {
  items: FollowUpStatusItem[];
  clientId: number;
  clientName: string;
  clientPhone?: string | null;
  clientCarId?: number;
  carLabel?: string;
  onScheduled?: () => void;
}

export const CarExpiredFollowUpReminder: React.FC<
  CarExpiredFollowUpReminderProps
> = ({
  items,
  clientId,
  clientName,
  clientPhone,
  clientCarId,
  carLabel,
  onScheduled,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [preFilledData, setPreFilledData] = useState<{
    clientId?: number;
    serviceId?: number;
    clientCarId?: number;
    subServiceIds?: number[];
  }>();

  if (items.length === 0) {
    return null;
  }

  const expiredCount = items.filter((item) => item.isExpired).length;
  const hasPhone = Boolean(clientPhone?.trim());
  const countLabel =
    expiredCount === 0
      ? items.length === 1
        ? "1 serviço com validade"
        : `${items.length} serviços com validade`
      : expiredCount === 1
        ? "1 serviço vencido"
        : `${expiredCount} serviços vencidos`;
  const buttonClassName =
    expiredCount > 0
      ? "relative inline-flex items-center justify-center h-9 w-9 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors flex-shrink-0"
      : "relative inline-flex items-center justify-center h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors flex-shrink-0";

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
        className={buttonClassName}
        title={`${countLabel}. Clique para ver a validade dos serviços.`}
        aria-label={countLabel}
      >
        <Icons.Phone sx={{ fontSize: 18 }} />
        {expiredCount > 0 ? (
          <span className="absolute -top-1.5 -right-1.5 min-w-[1.15rem] h-[1.15rem] px-1 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none">
            {expiredCount > 99 ? "99+" : expiredCount}
          </span>
        ) : null}
      </button>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Validade dos serviços"
        icon={<Icons.Phone sx={{ fontSize: 24 }} />}
        size="lg"
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">
            {countLabel} para {clientName}.
            {expiredCount > 0
              ? " Envie um lembrete ou agende o retorno dos serviços vencidos."
              : " Todos os serviços estão em dia."}
          </p>

          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden">
            {items.map((item) => {
              const daysSince = item.daysSinceLastAppointment ?? 0;
              const daysRemaining = item.expirationDays - daysSince;
              const scheduleServiceId = item.scheduleServiceId ?? item.id;
              const itemCarLabel =
                item.carLabel && item.carLabel !== carLabel
                  ? item.carLabel
                  : null;

              return (
                <li
                  key={`${item.kind}-${item.id}-${item.clientCarId ?? "none"}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        {item.isExpired ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            Vencido
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Em dia
                          </span>
                        )}
                      </div>
                      {itemCarLabel ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {itemCarLabel}
                        </p>
                      ) : null}
                      {item.lastAppointmentDate ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Último atendimento:{" "}
                          <span className="font-medium text-gray-700">
                            {formatDateForDisplay(item.lastAppointmentDate)}
                          </span>
                          {" · "}
                          {item.isExpired ? (
                            <span className="font-semibold text-amber-700">
                              {daysSince} dias atrás
                            </span>
                          ) : (
                            <span className="font-semibold text-emerald-700">
                              restam {daysRemaining}{" "}
                              {daysRemaining === 1 ? "dia" : "dias"}
                            </span>
                          )}
                        </p>
                      ) : null}
                      <p className="text-xs text-gray-400 mt-0.5">
                        Validade: {item.expirationDays} dias
                      </p>
                    </div>

                    {item.isExpired ? (
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        {hasPhone ? (
                          <button
                            type="button"
                            onClick={() =>
                              sendWhatsAppMessage({
                                phoneNumber: clientPhone!,
                                message: getFollowUpWhatsAppMessage(
                                  clientName,
                                  item.name,
                                  daysSince,
                                  item.expirationDays,
                                ),
                              })
                            }
                            className={getWhatsAppButtonStyles("sm")}
                            title="Enviar mensagem no WhatsApp"
                            aria-label={`Enviar lembrete de ${item.name} por WhatsApp`}
                          >
                            <svg
                              className={getWhatsAppIconStyles("sm")}
                              fill="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden
                            >
                              <path d={WHATSAPP_ICON_PATH} />
                            </svg>
                          </button>
                        ) : null}

                        <button
                          type="button"
                          onClick={() => {
                            setPreFilledData({
                              clientId,
                              serviceId: scheduleServiceId,
                              clientCarId: item.clientCarId ?? clientCarId,
                              subServiceIds:
                                item.kind === "subService"
                                  ? [item.id]
                                  : undefined,
                            });
                            setCreateModalOpen(true);
                          }}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                          title="Agendar atendimento"
                        >
                          <Icons.Event sx={{ fontSize: 16 }} />
                          <span>Agendar</span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Modal>

      {createModalOpen && (
        <CreateAppointmentModal
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setPreFilledData(undefined);
          }}
          onSuccess={() => {
            setCreateModalOpen(false);
            setPreFilledData(undefined);
            setModalOpen(false);
            onScheduled?.();
          }}
          selectedDate={getTodayInBrazil()}
          preFilledData={preFilledData}
        />
      )}
    </>
  );
};
